// First-run coach-mark tour: which steps it walks, and whether a given
// driver has already seen it.
//
// Completion lives in localStorage keyed by user id rather than in the DB
// BECAUSE a driver uses one phone — a per-user row would be a migration and
// a round trip to answer a question the device already knows. The cost is
// that clearing site data replays the tour once, which is harmless.

export type TourStep = {
  id: string;
  title: string;
  body: string;
  // CSS selector for the element to spotlight. A step whose target isn't on
  // the current screen renders as a plain centered card instead — the tour
  // never points at nothing.
  target: string;
};

// ~5 steps, in the order the driver's day runs: capture, report, then the
// three pricing behaviours that live on the review screen. Copy is shared
// with the self-playing explainer video, deliberately.
export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: "capture",
    title: "Capture load",
    body: "Snap a photo of each ticket — or upload a whole batch at once.",
    target: '[data-tour="capture"]',
  },
  {
    id: "report",
    title: "Report",
    body: "Your week, grouped and totaled — ready to send to dispatch.",
    target: '[data-tour="report"]',
  },
  {
    id: "default-rate",
    title: "Default rate",
    body: "Set your usual rate once. Every blank ticket fills in automatically.",
    target: '[data-tour="default-rate"]',
  },
  {
    id: "amount",
    title: "Your handwriting wins",
    body: "Wrote a different amount? SKIP reads your handwriting and uses it.",
    target: '[data-tour="amount"]',
  },
  {
    id: "send",
    title: "Send to Dispatch",
    body: "One tap sends the week to dispatch — before payday.",
    target: '[data-tour="send"]',
  },
];

// Only the two methods we use, so a test can pass a hand-rolled stub (or a
// throwing one) without standing up a whole Storage.
export type StorageLike = Pick<Storage, "getItem" | "setItem">;

export function tourStorageKey(userId: string): string {
  return `skip:tour:done:${userId}`;
}

// Safari private mode throws on both reads and writes, so every access is
// wrapped. A storage failure means "don't nag" — false, not a crash.
export function shouldShowTour(
  userId: string | null | undefined,
  storage: StorageLike | null | undefined,
): boolean {
  if (!userId || !storage) return false;
  try {
    return storage.getItem(tourStorageKey(userId)) === null;
  } catch {
    return false;
  }
}

export function markTourDone(
  userId: string | null | undefined,
  storage: StorageLike | null | undefined,
): void {
  if (!userId || !storage) return;
  try {
    storage.setItem(tourStorageKey(userId), "1");
  } catch {
    // Nothing to do — the in-memory guard in the component still stops a
    // second auto-open this session.
  }
}

// localStorage access itself can throw (not just its methods) when cookies
// are blocked, so even reaching for it is guarded.
export function safeLocalStorage(): StorageLike | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// A second, in-memory guard on top of the stored flag. If storage is
// unavailable (private mode) the write in markTourDone silently no-ops, and
// without this a remount would auto-open the tour again in the same session.
// Module-level rather than a ref BECAUSE it has to survive the component
// unmounting and remounting on a route change.
let autoShownThisSession = false;

export function tourAutoShownThisSession(): boolean {
  return autoShownThisSession;
}

export function markTourAutoShown(): void {
  autoShownThisSession = true;
}

// Test seam: the guard is process-wide, so cases that exercise auto-open
// have to start from a known state.
export function resetTourSessionGuard(): void {
  autoShownThisSession = false;
}
